import React, { useState } from "react";
import { connect } from "react-redux";
import { Button } from "devextreme-react/button";
import { DropDownButton, Popup, ScrollView } from "devextreme-react";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import CustomerInfoForm, { buildCustomerInfoLayout } from '../../../../id/common/customer/CustomerInfoForm';
import CustomerCredit from '../../../../id/common/customer/CustomerCredit';
import SummaryTab from '../../../../id/common/customer/SummaryTab';
import AdditionalTab from './AdditionalTab';
import CustomerOrders from './CustomerOrders';
import CreditHistoryTab from './CreditHistoryTab';
import CashReceiptsTab from './CashReceiptsTab';
import AROpenItemsTab from './AROpenItemsTab';
import { isValidGuid } from '../../../../common/Utils';
import { MESSAGES } from '../../../../common/Messages';
import CollectionCreditForm from '../../../../ar/components/collectionsCredit/Form';
import CustomerBrowseGrid from '../../../../ar/components/collectionsCredit/CustomerBrowseGrid';
import OpenFollowUpGrid from '../../../../ar/components/collectionsCredit/OpenFollowUpGrid';
import { FORM_LAYOUTS, GRID_KEYS, OM_RECORD_TYPE_OPEN, OM_RECORD_TYPE_HISTORY, SCREEN_NAME } from '../constants';
import '../../../../common/css/searchPage.css';

// Operations dropdown (mirrors salesOrderDataEntry/Form.js's Operations button): one
// entry today - Start Follow-up - kept as a list so more operations can be added later.
// Same label as AR's own listing button (MESSAGES.START_FOLLOW_UP) so the action is named
// the same wherever it is offered.
const OPERATIONS_ITEMS = [
    { id: 'StartFollowUp', name: MESSAGES.START_FOLLOW_UP },
];

// OMD0010 shows Cell Phone 1 rather than Modem in its customer header (SR-048749).
const CUSTOMER_INFO_LAYOUT = buildCustomerInfoLayout({
    contactField: "AddressCELLPHONE1",
    contactLabel: "Cell Phone 1",
});

/**
 * Detail view for one customer — the tab set of OMD0010's bottom page control:
 * Primary, Additional, Orders Open, Orders History, Credit History,
 * Cash Receipts and AR Open Items. The customer payload fetched by the Primary
 * tab is shared with the Additional tab so only one customer request is made.
 */
const OrdersByCustomerDetail = (props) => {
    const [customer, setCustomer] = useState(null);
    const [showCustomerCreditForm, setShowCustomerCreditForm] = useState(false);
    const [showSummaryForm, setShowSummaryForm] = useState(false);
    const [showStartFollowUpForm, setShowStartFollowUpForm] = useState(false);
    // Only pass a real customer key to the child grids (rule 5): a keyless/empty
    // Guid would have them query and focus rows for no parent.
    const customerId = isValidGuid(props.id) ? props.id : null;

    // Mirrors uARD0009's two top-level tabs (Open Follow-up / Customer). uOMD0010's own
    // opARCollectionsClick opens this screen and calls AddNewRecord (a blank Append) - the
    // popup always opens straight into add mode for this OM customer. The Open Follow-up
    // tab additionally lets the user browse ALL existing follow-ups and pick one to edit
    // (ARD0009's grdOpenFollowUpTop selection), and the Customer tab lets them browse
    // customers instead - both drive the same Followup/Details/History/etc. panel below.
    const [followUpTabIndex, setFollowUpTabIndex] = useState(0);
    const [followUpCustomerId, setFollowUpCustomerId] = useState(null);
    const [openFollowUpRowId, setOpenFollowUpRowId] = useState(null);
    const [customerTabCustomerId, setCustomerTabCustomerId] = useState(null);

    const handleOperationsItemClick = (e) => {
        if (e.itemData?.id === 'StartFollowUp' && customerId) {
            setFollowUpCustomerId(customerId);
            setOpenFollowUpRowId(null);
            setFollowUpTabIndex(0);
            setShowStartFollowUpForm(true);
        }
    };

    return (
        <div className="search-page-container">
            <div className="search-page-content">
                <Toolbar className="header">
                    <Item location="before">
                        <div className="title">
                            <h6 style={{ color: window._env_.Color2, marginLeft: '12px' }}>{SCREEN_NAME}</h6>
                        </div>
                    </Item>
                    <Item location="after">
                        <DropDownButton
                            className="accordianButton"
                            text="Operations"
                            icon="preferences"
                            dropDownOptions={{ width: 230 }}
                            items={OPERATIONS_ITEMS}
                            onItemClick={handleOperationsItemClick}
                            displayExpr="name"
                            keyExpr="id"
                            disabled={!customerId}
                        />
                    </Item>
                    <Item location="after">
                        <Button className="accordianButton" onClick={props.onBack} text="Back" />
                    </Item>
                </Toolbar>
                <TabPanel className="tabbed-detail-panel">
                    <TabItem title="Primary">
                        <div className="tabbed-detail-form">
                            <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                <Button
                                    text="Show Customer Summary"
                                    type="default"
                                    onClick={() => { if (props.id) setShowSummaryForm(true); }}
                                />
                                <Button
                                    text="Show Customer Credit Status"
                                    type="default"
                                    onClick={() => { if (props.id) setShowCustomerCreditForm(true); }}
                                />
                            </div>
                            <CustomerInfoForm
                                id={props.id}
                                layout={CUSTOMER_INFO_LAYOUT}
                                layoutId={FORM_LAYOUTS.Primary.id}
                                layoutName={FORM_LAYOUTS.Primary.name}
                                onCustomerLoaded={setCustomer}
                            />
                        </div>
                    </TabItem>
                    <TabItem title="Additional">
                        <div className="tabbed-detail-form">
                            <AdditionalTab id={props.id} customer={customer} />
                        </div>
                    </TabItem>
                    <TabItem title="Orders Open">
                        <CustomerOrders
                            customerId={customerId}
                            recordType={OM_RECORD_TYPE_OPEN}
                            gridKey={GRID_KEYS.OrdersOpen}
                        />
                    </TabItem>
                    <TabItem title="Orders History">
                        <CustomerOrders
                            customerId={customerId}
                            recordType={OM_RECORD_TYPE_HISTORY}
                            gridKey={GRID_KEYS.OrdersHistory}
                        />
                    </TabItem>
                    <TabItem title="Credit History">
                        <CreditHistoryTab
                            customerId={customerId}
                            gridKey={GRID_KEYS.CreditHistory}
                        />
                    </TabItem>
                    <TabItem title="Cash Receipts">
                        <CashReceiptsTab
                            customerId={customerId}
                            gridKey={GRID_KEYS.CashReceipts}
                        />
                    </TabItem>
                    <TabItem title="AR Open Items">
                        <AROpenItemsTab
                            customerId={customerId}
                            gridKey={GRID_KEYS.AROpenItems}
                        />
                    </TabItem>
                </TabPanel>
            </div>
            {showCustomerCreditForm && (
                <Popup
                    visible={showCustomerCreditForm}
                    onHiding={() => setShowCustomerCreditForm(false)}
                    width="90vw"
                    height="90vh"
                    title="Customer Credit Status"
                    showCloseButton={true}
                >
                    <CustomerCredit customerId={customerId} module="OM" />
                </Popup>
            )}
            {showSummaryForm && (
                <Popup
                    visible={showSummaryForm}
                    onHiding={() => setShowSummaryForm(false)}
                    width="60vw"
                    height="40vw"
                    title="Customer Summary Screen"
                    showCloseButton={true}
                >
                    <div style={{ height: "100%", overflow: "auto" }}>
                        <SummaryTab customerId={customerId} />
                    </div>
                </Popup>
            )}
            {showStartFollowUpForm && (
                <Popup
                    visible={showStartFollowUpForm}
                    onHiding={() => setShowStartFollowUpForm(false)}
                    width="90vw"
                    height="90vh"
                    title="AR Credit/Collections Follow-up"
                    showCloseButton={true}
                >
                    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                        <TabPanel
                            className="tabbed-detail-panel"
                            selectedIndex={followUpTabIndex}
                            onSelectionChanged={(e) => setFollowUpTabIndex(e.component.option('selectedIndex'))}
                        >
                            <TabItem title="Open Follow-up">
                                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                    <div style={{ flex: "0 0 260px", minHeight: 0 }}>
                                        <OpenFollowUpGrid
                                            onFollowUpChanged={(header) => setOpenFollowUpRowId(header?.RowId ?? null)}
                                        />
                                    </div>
                                    {/* devextreme's own scrollbar (only appears when content overflows) instead
                                        of a native overflow:auto div - matches customerDataEntry's own popup.
                                        No row selected in the grid above -> Form.js falls back to its
                                        customerId branch, i.e. add mode prefilled with the OM customer that
                                        opened this popup (matches uOMD0010's opARCollectionsClick ->
                                        AddNewRecord, a blank Append, when the screen first opens). Selecting
                                        an existing row there loads it for editing instead, same as ARD0009's
                                        qryARCOLLECTIONCREDITHEADERRead selection driving its bottom panel.
                                        No key here for the same DevExtreme teardown/rebuild reason as the
                                        Customer tab below - see that comment. */}
                                    <div style={{ flex: "1 1 auto", minHeight: 0 }}>
                                        <ScrollView height="100%" width="100%">
                                            <CollectionCreditForm id={openFollowUpRowId} customerId={followUpCustomerId} isPopup={true} />
                                        </ScrollView>
                                    </div>
                                </div>
                            </TabItem>
                            <TabItem title="Customer">
                                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                    <div style={{ flex: "0 0 260px", minHeight: 0 }}>
                                        <CustomerBrowseGrid
                                            onCustomerChanged={(customer) => setCustomerTabCustomerId(customer?.id ?? null)}
                                        />
                                    </div>
                                    {/* Always rendered - Form.js's own effect reacts to the customerId prop
                                        change and reloads the sub-tabs (Followup/Details/History/Open Items/
                                        Cash Receipts/Transactions/Contact) for the newly selected row, matching
                                        ARD0009's iCurrentCustomer-driven bottom panel. loadByCustomer is what
                                        makes the Followup/Details tabs load the selected customer's existing
                                        follow-up in full (legacy opens the header query on "R_Customer =
                                        iCurrentCustomer" here, with no ROWID clause) instead of only stamping
                                        the Customer field. Deliberately NOT keyed on
                                        customerTabCustomerId to force a remount: forcibly tearing down and
                                        rebuilding this whole tree of DevExtreme widgets in one React commit
                                        raced DevExtreme's own DOM cleanup and threw a removeChild
                                        NotFoundError that blanked the page. The trade-off is that switching
                                        customers here doesn't clear an in-progress, unsaved Followup/Details
                                        edit - click New/Reset on that tab if you want a blank record for the
                                        new customer. */}
                                    <div style={{ flex: "1 1 auto", minHeight: 0 }}>
                                        <ScrollView height="100%" width="100%">
                                            <CollectionCreditForm customerId={customerTabCustomerId} loadByCustomer={true} isPopup={true} />
                                        </ScrollView>
                                    </div>
                                </div>
                            </TabItem>
                        </TabPanel>
                    </div>
                </Popup>
            )}
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(OrdersByCustomerDetail);
